import { removeEntryFromRowToModificationMap, addEntryToRowToModificationMap, } from "owa-conditional-formatting";
import type { MailboxInfo } from "owa-client-types";
import { getMailboxKeyForMailboxInfo } from "owa-client-types";
import type { ConditionalFormattingModification } from "owa-conditional-formatting";

interface CacheUpdate {
    oldKey: string;
    newKey: string;
    modifications: ConditionalFormattingModification[];
    mailboxInfo: MailboxInfo;
}

// Use Map for deduplication by composite key
const pendingUpdates = new Map<string, CacheUpdate>();
let scheduledHandle: number | null = null;
let isProcessing = false;

function getUpdateKey(update: CacheUpdate): string {
    const mailboxKey = update.mailboxInfo
        ? getMailboxKeyForMailboxInfo(update.mailboxInfo)
        : "default";
    return `${mailboxKey}|${update.newKey}`;
}

export function queueCacheUpdate(update: CacheUpdate): void {
    // Deduplicate updates for the same row
    const key = getUpdateKey(update);
    pendingUpdates.set(key, update);
    
    if (!isProcessing && scheduledHandle === null) {
        scheduleFlush();
    }
}

function scheduleFlush(): void {
    // Use requestIdleCallback for better performance, fallback to setTimeout
    if ("requestIdleCallback" in window) {
        scheduledHandle = window.requestIdleCallback(() => flush(), { timeout: 100 } // Process within 100ms max
        ) as unknown as number;
    }
    else {
        // Use macro task (setTimeout) instead of microtask to allow paint between updates. 16 ms aligns with 60fps.
        scheduledHandle = setTimeout(() => flush(), 16) as unknown as number;
    }
}

function flush(): void {
    scheduledHandle = null;
    
    // Prevent re-entrant processing
    if (isProcessing || pendingUpdates.size === 0) {
        return;
    }
    
    isProcessing = true;
    
    // Take snapshot of current updates and clear queue
    const updates = Array.from(pendingUpdates.values());
    pendingUpdates.clear();
    
    try {
        // Batch process all updates
        for (const update of updates) {
            const { oldKey, newKey, modifications, mailboxInfo } = update;
            if (oldKey) {
                removeEntryFromRowToModificationMap(oldKey, mailboxInfo, "Mail");
            }
            addEntryToRowToModificationMap(newKey, modifications, mailboxInfo, "Mail");
        }
    }
    finally {
        isProcessing = false;
        
        // If new updates were queued during processing, schedule another flush
        if (pendingUpdates.size > 0 && scheduledHandle === null) {
            scheduleFlush();
        }
    }
}
